import {
  Box,
  CircularProgress,
  FormControl,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserRoleEnum, useUserContext } from '../../shared/contexts';
import { LayoutShell, LayoutRegister } from '../../shared/layouts';
import { IEmployeeView, IBusinessEmployeeView, employeeService, businessService } from '../../shared/services';
import { EmployeeInformation, EmployeeDashboard, EmployeeWallet, EmployeeCommissions } from './pages';
import { IBusinessView } from '../../shared/services';
import { EmployeeOrderTickets } from './pages/EmployeeOrderTickets';
// Tabs de business foram movidas para /business
import { Forbidden } from '../../shared/components';
import { useTabNavigation } from '../../shared/hooks/useTabNavigation';

export enum EmployeeTabsEnum {
  Information = 0,
  OrderTickets = 1,
  Dashboard = 2,
  Wallet = 3,
  Commissions = 4,
}

const EMPLOYEE_TAB_LABELS: Record<number, string> = {
  [EmployeeTabsEnum.Information]: 'Informações',
  [EmployeeTabsEnum.OrderTickets]: 'Comandas',
  [EmployeeTabsEnum.Dashboard]: 'Dashboard',
  [EmployeeTabsEnum.Wallet]: 'Carteira',
  [EmployeeTabsEnum.Commissions]: 'Comissões',
};

const EMPLOYEE_TAB_ICONS: Record<number, string> = {
  [EmployeeTabsEnum.Information]: 'business',
  [EmployeeTabsEnum.OrderTickets]: 'receipt',
  [EmployeeTabsEnum.Dashboard]: 'bar_chart',
  [EmployeeTabsEnum.Wallet]: 'account_balance_wallet',
  [EmployeeTabsEnum.Commissions]: 'monetization_on',
};

export interface IPropsEmployeePage {
  employee: IEmployeeView,
  refetch: () => void,
  business?: IBusinessView,
  selectedBizEmp?: IBusinessEmployeeView,
}

export const Employee: React.FC = () => {
  const { userId, userRoles } = useUserContext();
  const location = useLocation();
  const navigate = useNavigate();

  const EMPLOYEE_TABS = useMemo(() => [
    EmployeeTabsEnum.Information,
    EmployeeTabsEnum.OrderTickets,
    EmployeeTabsEnum.Dashboard,
    EmployeeTabsEnum.Wallet,
    EmployeeTabsEnum.Commissions,
  ], []);

  const { tabIndex, visualIndex, setTabIndex } = useTabNavigation(EMPLOYEE_TABS, EmployeeTabsEnum.Information);

  const { data: employee, refetch, isFetching } = useQuery(
    ['employee'],
    () => employeeService.getByUserId(userId),
  );

  const [selectedBizEmpId, setSelectedBizEmpId] = useState<number | null>(null);

  const selectedBizEmp = useMemo(() => {
    const items = employee?.businessEmployees ?? [];
    if (!items.length) return undefined;
    if (selectedBizEmpId) {
      return items.find(be => be.id === selectedBizEmpId) ?? items[0];
    }
    return items[0];
  }, [employee, selectedBizEmpId]);

  const businessId = useMemo(() => selectedBizEmp?.businessId, [selectedBizEmp]);

  const isReceptionist = userRoles.includes(UserRoleEnum.Receptionist);

  const { data: business } = useQuery({
    queryKey: ['business', businessId],
    queryFn: () => businessId ? businessService.getById(businessId) : null,
    enabled: !!businessId && isReceptionist,
  });

  // Recarrega dados do employee ao trocar de business
  useEffect(() => {
    if (selectedBizEmpId) {
      refetch();
    }
  }, [selectedBizEmpId, refetch]);

  // Navegação vinda do sidebar (location.state.page = enum value)
  useEffect(() => {
    const state = location.state as { page?: number } | null;
    if (state?.page !== undefined) {
      const visualIdx = EMPLOYEE_TABS.indexOf(state.page as EmployeeTabsEnum);
      if (visualIdx >= 0) {
        setTabIndex(state.page as EmployeeTabsEnum);
      }
    }
  }, [location.state, EMPLOYEE_TABS, setTabIndex]);

  const onChangeTab = useCallback((_: React.SyntheticEvent, index: number) => {
    const enumValue = EMPLOYEE_TABS[index];
    if (enumValue !== undefined) {
      navigate('/employee', { state: { page: enumValue }, replace: true });
    }
  }, [EMPLOYEE_TABS, navigate]);

  const renderContent = useCallback(() => {
    if (!employee) { return null; }

    switch (tabIndex) {
    case EmployeeTabsEnum.Information:
      return <EmployeeInformation employee={employee} refetch={refetch} business={business ?? undefined} selectedBizEmp={selectedBizEmp} />;
    case EmployeeTabsEnum.OrderTickets:
      return <EmployeeOrderTickets employee={employee} refetch={refetch} business={business ?? undefined} selectedBizEmp={selectedBizEmp} />;
    case EmployeeTabsEnum.Dashboard:
      return <EmployeeDashboard employee={employee} refetch={refetch} business={business ?? undefined} selectedBizEmp={selectedBizEmp} />;
    case EmployeeTabsEnum.Wallet:
      return <EmployeeWallet employee={employee} refetch={refetch} selectedBizEmp={selectedBizEmp} />;
    case EmployeeTabsEnum.Commissions:
      return <EmployeeCommissions employee={employee} refetch={refetch} selectedBizEmp={selectedBizEmp} />;
    default:
      return null;
    }
  }, [employee, business, tabIndex, refetch]);

  const isAllowed = userRoles.includes(UserRoleEnum.Employee) || isReceptionist;

  return isAllowed ? (
    <LayoutShell>
      {isFetching ? (
        <Box display='flex' justifyContent='center' py={6}><CircularProgress /></Box>
      ) : employee ? (
        <LayoutRegister
          header={
            <Box>
              {employee && employee.businessEmployees.length > 1 && (
                <Box display='flex' alignItems='center' px={2} py={1}>
                  <FormControl sx={{ minWidth: 250 }}>
                    <InputLabel id='bizemp-select-label'>Estabelecimento</InputLabel>
                    <Select
                      labelId='bizemp-select-label'
                      value={selectedBizEmp?.id ?? ''}
                      label='Estabelecimento'
                      onChange={(e) => setSelectedBizEmpId(e.target.value as number)}
                    >
                      {employee.businessEmployees.map((be) => (
                        <MenuItem key={be.id} value={be.id}>
                          {be.business?.name || `Business #${be.businessId}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              <Tabs value={visualIndex} onChange={onChangeTab} variant='scrollable'>
                {EMPLOYEE_TABS.map((t) => (
                  <Tab key={t} label={EMPLOYEE_TAB_LABELS[t]} icon={<Icon>{EMPLOYEE_TAB_ICONS[t]}</Icon>} iconPosition='start' />
                ))}
              </Tabs>
            </Box>
          }
          hideSaveButton
        >
          {renderContent()}
        </LayoutRegister>
      ) : (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          Cadastrar employee
        </Box>
      )}
    </LayoutShell>
  ) : (
    <Forbidden />
  );
};
